import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Navigation } from "@/components/Navigation";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Wand2, Download, Loader2, Image as ImageIcon, Copy, Check, FileImage } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useStreamingVisualCreator } from "@/hooks/useStreamingVisualCreator";
import UpgradeDialog from "@/components/UpgradeDialog";

interface GeneratedVisual {
  svg: string;
  dataUrl: string;
  prompt: string;
}

const getAspectRatioInfo = (aspectRatio: string) => {
  const ratioMap: Record<string, { description: string; dimensions: string; layoutGuidance: string }> = {
    '1:1': {
      description: 'square format',
      dimensions: '800x800',
      layoutGuidance: 'Center elements symmetrically. Use circular or grid layouts. Perfect for logos, icons, or balanced compositions.'
    },
    '16:9': {
      description: 'landscape/widescreen format',
      dimensions: '960x540',
      layoutGuidance: 'Utilize the horizontal space with wide layouts. Place key elements across the width. Great for timelines, process flows, or panoramic visuals.'
    },
    '9:16': {
      description: 'portrait/mobile format',
      dimensions: '540x960',
      layoutGuidance: 'Stack elements vertically to use the tall format effectively. Create a top-to-bottom flow. Perfect for mobile-friendly infographics, vertical timelines, or step-by-step processes.'
    },
    '4:3': {
      description: 'standard/traditional format', 
      dimensions: '800x600',
      layoutGuidance: 'Classic balanced layout. Works well for most visual types with good proportions.'
    },
    '3:4': {
      description: 'tall portrait format',
      dimensions: '600x800', 
      layoutGuidance: 'Emphasize vertical flow and stacking. Great for lists, hierarchies, or portrait-oriented designs.'
    }
  };
  
  return ratioMap[aspectRatio] || ratioMap['4:3'];
};

const VisualCreator = () => {
  const { user } = useAuth();
  const { streamingState, streamGenerateVisual, reset: resetStreaming } = useStreamingVisualCreator();
  const [prompt, setPrompt] = useState("");
  const [visualType, setVisualType] = useState("auto");
  const [selectedVisualTypes, setSelectedVisualTypes] = useState<string[]>([]);
  
  const toggleVisualType = (type: string) => {
    if (type === 'auto') {
      setSelectedVisualTypes([]);
      setVisualType('auto');
    } else {
      if (selectedVisualTypes.includes(type)) {
        setSelectedVisualTypes(prev => prev.filter(t => t !== type));
      } else {
        if (selectedVisualTypes.length >= 4) {
          toast.error("Maximum 4 visual types can be selected");
          return;
        }
        setSelectedVisualTypes(prev => [...prev, type]);
      }
      setVisualType('multi');
    }
  };
  const [aspectRatio, setAspectRatio] = useState("4:3");
  const [numOutputs, setNumOutputs] = useState("1");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVisuals, setGeneratedVisuals] = useState<GeneratedVisual[]>([]);
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [useStreaming, setUseStreaming] = useState(true);

  const visualTypes = [
    { value: "auto", label: "Auto (AI Decides)" },
    // Data Visualization
    { value: "mindmap", label: "Mind Map" },
    { value: "flowchart", label: "Flowchart" },
    { value: "table", label: "Table/Chart" },
    { value: "timeline", label: "Timeline" },
    { value: "org-chart", label: "Organization Chart" },
    { value: "process-diagram", label: "Process Diagram" },
    { value: "network-diagram", label: "Network Diagram" },
    { value: "pie-chart", label: "Pie Chart" },
    { value: "bar-chart", label: "Bar Chart" },
    { value: "line-graph", label: "Line Graph" },
    { value: "scatter-plot", label: "Scatter Plot" },
    { value: "venn-diagram", label: "Venn Diagram" },
    { value: "gantt-chart", label: "Gantt Chart" },
    { value: "decision-tree", label: "Decision Tree" },
    { value: "hierarchy", label: "Hierarchy Diagram" },
    { value: "comparison-chart", label: "Comparison Chart" },
    { value: "cycle-diagram", label: "Cycle Diagram" },
    { value: "checklist", label: "Checklist" },
    // Creative Visuals
    { value: "infographic", label: "Infographic" },
    { value: "photo", label: "Photograph" },
    { value: "illustration", label: "Illustration" },
    { value: "digital-art", label: "Digital Art" },
    { value: "painting", label: "Painting" },
    { value: "sketch", label: "Sketch" },
    { value: "logo", label: "Logo" },
    { value: "icon", label: "Icon" }
  ];

  const aspectRatios = [
    { value: "1:1", label: "Square (1:1)", size: "800x800" },
    { value: "16:9", label: "Landscape (16:9)", size: "960x540" },
    { value: "9:16", label: "Portrait (9:16)", size: "540x960" },
    { value: "4:3", label: "Standard (4:3)", size: "800x600" },
    { value: "3:4", label: "Tall (3:4)", size: "600x800" }
  ];

  const handleGenerate = async () => {
    if (!user) {
      toast.error("Please sign in to generate visuals");
      return;
    }

    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    // Pre-check credits
    try {
      const { data: sub, error } = await supabase
        .from('user_subscriptions')
        .select('credits_remaining, status')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
      }

      const creditsNeeded = selectedVisualTypes.length > 0 
        ? selectedVisualTypes.length 
        : (useStreaming ? 1 : parseInt(numOutputs)); // Streaming only supports 1 unless multi-types selected
      // Check if user needs upgrade: no subscription OR (has limited credits AND not enough remaining)
      if (!sub || (sub.credits_remaining !== null && sub.credits_remaining < creditsNeeded)) {
        setShowUpgradeDialog(true);
        toast.error(`You need ${creditsNeeded} credit${creditsNeeded > 1 ? "s" : ""} to generate ${creditsNeeded} visual${creditsNeeded > 1 ? "s" : ""}. Please upgrade to continue.`);
        return;
      }
    } catch (e) {
      console.error('Subscription pre-check failed:', e);
    }

    // If multiple visual types are selected, generate one per type in parallel (non-streaming)
    if (selectedVisualTypes.length > 0) {
      setIsGenerating(true);
      setGeneratedVisuals([]);

      try {
        const results = await Promise.all(
          selectedVisualTypes.map(async (type) => {
            // Create aspect ratio aware prompt
            const aspectRatioInfo = getAspectRatioInfo(aspectRatio);
            const enhancedPrompt = `${prompt}

ASPECT RATIO REQUIREMENTS: Create this ${type} visual specifically optimized for ${aspectRatioInfo.description} (${aspectRatio} ratio, ${aspectRatioInfo.dimensions}). 
${aspectRatioInfo.layoutGuidance}`;

            const { data, error } = await supabase.functions.invoke('generate-visual', {
              body: {
                prompt: enhancedPrompt,
                visualType: type,
                aspectRatio,
                numOutputs: 1,
              }
            });
            if (error) throw error;
            const visualSvg = data?.visuals?.[0]?.svg || data?.svg;
            const visualDataUrl = data?.visuals?.[0]?.dataUrl || data?.dataUrl;
            if (!visualSvg) throw new Error('No visual was generated');

            // Update credits if response contains it
            if (data?.credits_remaining !== undefined) {
              window.dispatchEvent(new CustomEvent('creditsUpdated', {
                detail: { credits_remaining: data.credits_remaining }
              }));
            }

            return { svg: visualSvg, dataUrl: visualDataUrl, prompt };
          })
        );

        setGeneratedVisuals(results);
        toast.success(`Generated ${results.length} visual${results.length > 1 ? 's' : ''} successfully!`);
      } catch (error) {
        console.error('Error generating multiple types:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to generate visuals';
        if (String(errorMessage).includes('No active subscription') || String(errorMessage).includes('No credits remaining')) {
          setShowUpgradeDialog(true);
        }
        toast.error(errorMessage);
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    if (useStreaming) {
      // Use streaming approach
      resetStreaming();
      try {
        // Create aspect ratio aware prompt for streaming
        const aspectRatioInfo = getAspectRatioInfo(aspectRatio);
        const enhancedPrompt = `${prompt}

ASPECT RATIO REQUIREMENTS: Create this visual specifically optimized for ${aspectRatioInfo.description} (${aspectRatio} ratio, ${aspectRatioInfo.dimensions}). 
${aspectRatioInfo.layoutGuidance}`;

        const result = await streamGenerateVisual(enhancedPrompt, visualType, aspectRatio);
        if (result) {
          setGeneratedVisuals([{
            svg: result.svg,
            dataUrl: result.dataUrl,
            prompt
          }]);
          toast.success("Visual generated successfully!");
        }
      } catch (error) {
        console.error("Error streaming visual:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to generate visual";
        
        if (errorMessage.includes('No active subscription') || errorMessage.includes('No credits remaining')) {
          setShowUpgradeDialog(true);
          toast.error(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      }
    } else {
      // Use original non-streaming approach
      setIsGenerating(true);
      setGeneratedVisuals([]);

       try {
         // Create aspect ratio aware prompt for non-streaming
         const aspectRatioInfo = getAspectRatioInfo(aspectRatio);
         const enhancedPrompt = `${prompt}

ASPECT RATIO REQUIREMENTS: Create this visual specifically optimized for ${aspectRatioInfo.description} (${aspectRatio} ratio, ${aspectRatioInfo.dimensions}). 
${aspectRatioInfo.layoutGuidance}`;

         const { data, error } = await supabase.functions.invoke('generate-visual', {
           body: {
             prompt: enhancedPrompt,
             visualType,
             aspectRatio,
             numOutputs: parseInt(numOutputs)
           }
         });

        if (error) {
          throw error;
        }

        // Handle both single and multiple visual responses
        if (data?.visuals) {
          // Multiple visuals response
          setGeneratedVisuals(data.visuals.map((v: any) => ({
            svg: v.svg,
            dataUrl: v.dataUrl,
            prompt
          })));
        } else if (data?.svg) {
          // Single visual response (backward compatibility)
          setGeneratedVisuals([{
            svg: data.svg,
            dataUrl: data.dataUrl,
            prompt
          }]);
        } else {
          throw new Error('No visuals were generated');
        }

        // Update credits display
        if (data.credits_remaining !== undefined) {
          window.dispatchEvent(new CustomEvent('creditsUpdated', {
            detail: { credits_remaining: data.credits_remaining }
          }));
        }

        const count = data?.visuals?.length || 1;
        toast.success(`Generated ${count} visual${count > 1 ? 's' : ''} successfully!`);
      } catch (error) {
        console.error("Error generating visuals:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to generate visuals";
        
        if (errorMessage.includes('No active subscription') || errorMessage.includes('No credits remaining')) {
          setShowUpgradeDialog(true);
          toast.error(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsGenerating(false);
      }
    }
  };

  const copyToClipboard = async (svgContent: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(svgContent);
      setCopiedItems(prev => new Set(prev).add(itemId));
      toast.success("SVG code copied to clipboard!");
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const sanitizeSvg = (input: string) => {
    let svg = input.trim();
    svg = svg.replace(/<script[\s\S]*?<\/script>/gi, '');
    svg = svg.replace(/<a[\s\S]*?<\/a>/gi, (match) => {
      const content = match.replace(/<\/?a[^>]*>/gi, '');
      return content;
    });
    svg = svg.replace(/\s+href\s*=\s*["'][^"']*["']/gi, '');
    svg = svg.replace(/\s+xlink:href\s*=\s*["'][^"']*["']/gi, '');
    svg = svg.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
    
    // Make SVG responsive: width 100%, height auto, preserve aspect ratio
    svg = svg.replace(/<svg([^>]*)>/i, (m, attrs) => {
      let a = attrs || '';
      if (!/xmlns=/.test(a)) a = a + ' xmlns="http://www.w3.org/2000/svg"';
      if (!/xmlns:xlink=/.test(a)) a = a + ' xmlns:xlink="http://www.w3.org/1999/xlink"';
      a = a.replace(/\swidth="[^"]*"/i, '');  
      a = a.replace(/\sheight="[^"]*"/i, '');
      const hasPAR = /preserveAspectRatio=/i.test(a);
      const par = hasPAR ? '' : ' preserveAspectRatio="xMidYMid meet"';
      const styleAttr = /style=/i.test(a) ? '' : ' style="max-width:100%;height:auto;overflow:visible;"';
      return `<svg${a} width="100%" height="auto"${par}${styleAttr}>`;
    });

    // Fix text positioning and overflow issues
    svg = svg.replace(/<text([^>]*)>/gi, (m, attrs) => {
      if (!/text-anchor/.test(attrs)) {
        attrs += ' text-anchor="start"';
      }
      if (!/dominant-baseline/.test(attrs)) {
        attrs += ' dominant-baseline="auto"';
      }
      return `<text${attrs} style="overflow:visible;word-wrap:break-word;">`;
    });

    svg = svg.replace(/&(?!amp;|lt;|gt;|quot;|apos;|#\d+;|#x[0-9A-Fa-f]+;)/g, '&amp;');
    return svg;
  };

  const downloadSvg = async (index: number) => {
    try {
      const v = generatedVisuals[index];
      let cleanSvg = sanitizeSvg(v.svg.trim());
      
      // Ensure proper SVG structure
      if (!cleanSvg.startsWith('<?xml')) {
        cleanSvg = '<?xml version="1.0" encoding="UTF-8"?>\n' + cleanSvg;
      }
      
      const blob = new Blob([cleanSvg], { type: 'image/svg+xml;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `visual-${Date.now()}-${index + 1}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("SVG downloaded!");
    } catch (error) {
      toast.error("Failed to download SVG");
    }
  };

  // Add project context tracking
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);

  // Check for project context in sessionStorage
  useEffect(() => {
    const projectId = sessionStorage.getItem('currentProjectId');
    if (projectId) {
      setCurrentProjectId(projectId);
    }
  }, []);

  // Save project context when project is created/updated
  useEffect(() => {
    if (currentProjectId) {
      sessionStorage.setItem('currentProjectId', currentProjectId);
    } else {
      sessionStorage.removeItem('currentProjectId');
    }
  }, [currentProjectId]);

  const saveToContent = async (visual: GeneratedVisual, index: number, existingProjectId?: string) => {
    if (!user) {
      toast.error("Please sign in to save content");
      return;
    }

    try {
      // Check if we should add to existing project or create new one
      if (existingProjectId) {
        // Add as new version to existing project
        const { data: existing, error: fetchError } = await supabase
          .from('content_items')
          .select('data, version, series_id')
          .eq('id', existingProjectId)
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (fetchError) throw fetchError;
        
        if (existing) {
          await updateProjectWithVisuals(existingProjectId, [{ svg: visual.svg, dataUrl: visual.dataUrl }]);
          return;
        }
      }
      
      // Create new project
      // Generate a consistent series_id for this visual
      const seriesId = crypto.randomUUID();

      const { error } = await supabase
        .from('content_items')
        .insert({
          user_id: user.id,
          kind: 'visual',
          series_id: seriesId,
          version: 1,
          title: `Visual ${index + 1}`,
          prompt: visual.prompt,
          data: {
            svg: visual.svg,
            dataUrl: visual.dataUrl,
            aspectRatio,
            visualType
          }
        });

      if (error) throw error;
      toast.success("Visual saved to My Content!");
    } catch (error) {
      console.error('Error saving visual:', error);
      toast.error("Failed to save visual");
    }
  };

  const updateProjectWithVisuals = async (projectId: string, visuals: Array<{ svg: string; dataUrl?: string | null }>) => {
    if (!user) return;
    try {
      const { data: existing, error: fetchError } = await supabase
        .from('content_items')
        .select('data, version, series_id')
        .eq('id', projectId)
        .eq('user_id', user.id)
        .maybeSingle();
      if (fetchError) throw fetchError;
      
      if (!existing) {
        throw new Error('Project not found');
      }
      
      const existingData = (existing as any)?.data || {};
      const existingVisuals = Array.isArray(existingData.extractedVisuals) ? existingData.extractedVisuals : [];
      const nextVersionNumber = (existingVisuals.length || 0) + 1;
      
      const newVisualEntries = visuals.map(v => ({
        version: nextVersionNumber,
        svg: v.svg,
        dataUrl: v.dataUrl ?? null,
        created_at: new Date().toISOString()
      }));
      
      const mergedData = {
        ...existingData,
        extractedVisuals: [...existingVisuals, ...newVisualEntries],
      };
      
      const { error: updateError } = await supabase
        .from('content_items')
        .update({
          data: mergedData,
          version: ((existing as any)?.version ?? 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId)
        .eq('user_id', user.id);
      if (updateError) throw updateError;
      
      console.log(`Successfully updated project ${projectId} with version ${nextVersionNumber} visuals`);
      toast.success(`Project updated with version ${nextVersionNumber} visuals!`);
    } catch (error) {
      console.error('Error updating project with visuals:', error);
      toast.error('Failed to update project with new visuals');
    }
  };

  const requestFixUpdate = async (visual: GeneratedVisual, index: number) => {
    const fixPrompt = window.prompt("Describe what you'd like to fix or update about this visual:");
    if (!fixPrompt?.trim()) return;

    try {
      const fullPrompt = `Fix/update this visual based on user feedback: "${fixPrompt}"\n\nOriginal prompt: ${visual.prompt}`;
      
      resetStreaming();
      const result = await streamGenerateVisual(fullPrompt, visualType, aspectRatio);
      
      if (result) {
        // Create new version
        const newVisual = {
          svg: result.svg,
          dataUrl: result.dataUrl,
          prompt: fullPrompt
        };
        
        // Add to beginning of array as newest version
        setGeneratedVisuals(prev => [newVisual, ...prev]);
        
        // Save new version to database if user wants
        if (user) {
          const shouldSave = confirm("Save this updated version to My Content?");
          if (shouldSave) {
            await saveToContent(newVisual, 0, currentProjectId);
          }
        }
        
        toast.success("Visual updated successfully!");
      }
    } catch (error) {
      console.error("Error updating visual:", error);
      toast.error("Failed to update visual");
    }
  };

  const downloadPng = async (index: number) => {
    try {
      const v = generatedVisuals[index];

      // Determine export size from selected aspect ratio
      const { dimensions } = getAspectRatioInfo(aspectRatio);
      const [expW, expH] = dimensions.split('x').map((n) => parseInt(n, 10));
      const exportWidth = Number.isFinite(expW) ? expW : 1200;
      const exportHeight = Number.isFinite(expH) ? expH : 900;

      // Dynamic import html2canvas
      const html2canvas = (await import('html2canvas')).default;

      // Create a temporary container off-screen
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'fixed';
      tempContainer.style.left = '-10000px';
      tempContainer.style.top = '-10000px';
      tempContainer.style.width = `${exportWidth}px`;
      tempContainer.style.height = `${exportHeight}px`;
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.display = 'block';
      tempContainer.style.overflow = 'hidden';

      // Ensure long words wrap if the visual uses HTML via foreignObject
      tempContainer.style.wordBreak = 'break-word';
      tempContainer.style.overflowWrap = 'anywhere';

      // Process SVG to fix FontAwesome icons
      let processedSvg = v.svg;
      
      // Convert FontAwesome unicode characters to proper display symbols
      const fontAwesomeMap: Record<string, string> = {
        'F054': '▶', // chevron-right
        'F611': '📱', // mobile-phone
        'F108': '📱', // mobile-alt
        'F042': '🎯', // bullseye/target
        'F053': '◀', // chevron-left
        'F055': '⊕', // plus-circle
        'F056': '⊖', // minus-circle
        'F058': '✅', // check-circle
        'F057': '❌', // times-circle
        'F011': '⏻', // power-off
        'F013': '⚙️', // cog/settings
        'F015': '🏠', // home
        'F007': '👤', // user
        'F2BD': '👥', // user-friends
        'F0E0': '📧', // envelope
        'F002': '🔍', // search
        'F019': '📥', // download
        'F093': '📤', // upload
        'F021': '🔄', // sync
        'F017': '⏰', // clock
        'F073': '📅', // calendar
        'F041': '📍', // map-marker
        'F0C9': '☰', // bars (menu)
        'F00C': '✅', // check
        'F00D': '❌', // times
        'F067': '➕', // plus
        'F068': '➖', // minus
        'F129': 'ℹ️', // info
        'F12A': '❗', // exclamation
        'F071': '⚠️', // warning
        'F06A': '❓', // question
        'F04B': '▶️', // play
        'F04C': '⏸️', // pause
        'F04D': '⏹️', // stop
        'F004': '❤️', // heart
        'F005': '⭐', // star
        'F006': '☆', // star-o
        'F0A4': '🔗', // external-link
        'F064': '👆', // hand-point-up
        'F25A': '👍', // thumbs-up
        'F165': '👎', // thumbs-down
        'F0AC': '🌐', // globe
        'F0E7': '⚡', // bolt
        'F0F3': '🔔', // bell
        'F0A2': '📞', // phone
        'F0C0': '👥', // users  
        'F0F6': '🔒', // lock
        'F09C': '🔓', // unlock
        'F084': '🔑', // key
        'F1C0': '💾', // save
        'F044': '✏️', // edit
        'F014': '🗑️', // trash
        'F024': '📢', // bullhorn
        'F0A0': '📋', // clipboard
        'F1C5': '📁', // folder-open
        'F07B': '📁', // folder
        'F03E': '🖼️', // image
        'F016': '📄', // file
        'F028': '🔊', // volume-up
        'F027': '🔉', // volume-down
        'F026': '🔇', // volume-off
        'F1CC': '📝', // file-code
        'F0C5': '📑', // copy
        'F24D': '👁️', // eye
        'F070': '🙈', // eye-slash
      };

      // Replace FontAwesome unicode references with symbols
      Object.entries(fontAwesomeMap).forEach(([unicode, symbol]) => {
        // Match the unicode patterns more broadly
        const patterns = [
          new RegExp(`\\b${unicode}\\b`, 'gi'),
          new RegExp(`&#x${unicode.toLowerCase()};`, 'gi'),
          new RegExp(`\\\\${unicode}`, 'gi'),
        ];
        patterns.forEach(pattern => {
          processedSvg = processedSvg.replace(pattern, symbol);
        });
      });

      // Also replace common FontAwesome CSS classes with symbols
      const classReplacements = [
        { pattern: /<i[^>]*class="[^"]*fa-chevron-right[^"]*"[^>]*><\/i>/gi, replacement: '▶' },
        { pattern: /<i[^>]*class="[^"]*fa-chevron-left[^"]*"[^>]*><\/i>/gi, replacement: '◀' },
        { pattern: /<i[^>]*class="[^"]*fa-mobile[^"]*"[^>]*><\/i>/gi, replacement: '📱' },
        { pattern: /<i[^>]*class="[^"]*fa-phone[^"]*"[^>]*><\/i>/gi, replacement: '📞' },
        { pattern: /<i[^>]*class="[^"]*fa-envelope[^"]*"[^>]*><\/i>/gi, replacement: '📧' },
        { pattern: /<i[^>]*class="[^"]*fa-home[^"]*"[^>]*><\/i>/gi, replacement: '🏠' },
        { pattern: /<i[^>]*class="[^"]*fa-user[^"]*"[^>]*><\/i>/gi, replacement: '👤' },
        { pattern: /<i[^>]*class="[^"]*fa-users[^"]*"[^>]*><\/i>/gi, replacement: '👥' },
        { pattern: /<i[^>]*class="[^"]*fa-search[^"]*"[^>]*><\/i>/gi, replacement: '🔍' },
        { pattern: /<i[^>]*class="[^"]*fa-download[^"]*"[^>]*><\/i>/gi, replacement: '📥' },
        { pattern: /<i[^>]*class="[^"]*fa-upload[^"]*"[^>]*><\/i>/gi, replacement: '📤' },
        { pattern: /<i[^>]*class="[^"]*fa-clock[^"]*"[^>]*><\/i>/gi, replacement: '⏰' },
        { pattern: /<i[^>]*class="[^"]*fa-calendar[^"]*"[^>]*><\/i>/gi, replacement: '📅' },
        { pattern: /<i[^>]*class="[^"]*fa-map-marker[^"]*"[^>]*><\/i>/gi, replacement: '📍' },
        { pattern: /<i[^>]*class="[^"]*fa-bars[^"]*"[^>]*><\/i>/gi, replacement: '☰' },
        { pattern: /<i[^>]*class="[^"]*fa-check[^"]*"[^>]*><\/i>/gi, replacement: '✅' },
        { pattern: /<i[^>]*class="[^"]*fa-times[^"]*"[^>]*><\/i>/gi, replacement: '❌' },
        { pattern: /<i[^>]*class="[^"]*fa-plus[^"]*"[^>]*><\/i>/gi, replacement: '➕' },
        { pattern: /<i[^>]*class="[^"]*fa-minus[^"]*"[^>]*><\/i>/gi, replacement: '➖' },
        { pattern: /<i[^>]*class="[^"]*fa-info[^"]*"[^>]*><\/i>/gi, replacement: 'ℹ️' },
        { pattern: /<i[^>]*class="[^"]*fa-warning[^"]*"[^>]*><\/i>/gi, replacement: '⚠️' },
        { pattern: /<i[^>]*class="[^"]*fa-question[^"]*"[^>]*><\/i>/gi, replacement: '❓' },
        { pattern: /<i[^>]*class="[^"]*fa-heart[^"]*"[^>]*><\/i>/gi, replacement: '❤️' },
        { pattern: /<i[^>]*class="[^"]*fa-star[^"]*"[^>]*><\/i>/gi, replacement: '⭐' },
        { pattern: /<i[^>]*class="[^"]*fa-lock[^"]*"[^>]*><\/i>/gi, replacement: '🔒' },
        { pattern: /<i[^>]*class="[^"]*fa-unlock[^"]*"[^>]*><\/i>/gi, replacement: '🔓' },
        { pattern: /<i[^>]*class="[^"]*fa-key[^"]*"[^>]*><\/i>/gi, replacement: '🔑' },
        { pattern: /<i[^>]*class="[^"]*fa-edit[^"]*"[^>]*><\/i>/gi, replacement: '✏️' },
        { pattern: /<i[^>]*class="[^"]*fa-trash[^"]*"[^>]*><\/i>/gi, replacement: '🗑️' },
        { pattern: /<i[^>]*class="[^"]*fa-eye[^"]*"[^>]*><\/i>/gi, replacement: '👁️' },
        { pattern: /<i[^>]*class="[^"]*fa-copy[^"]*"[^>]*><\/i>/gi, replacement: '📑' },
        { pattern: /<i[^>]*class="[^"]*fa-save[^"]*"[^>]*><\/i>/gi, replacement: '💾' },
        { pattern: /<i[^>]*class="[^"]*fa-folder[^"]*"[^>]*><\/i>/gi, replacement: '📁' },
        { pattern: /<i[^>]*class="[^"]*fa-file[^"]*"[^>]*><\/i>/gi, replacement: '📄' },
        { pattern: /<i[^>]*class="[^"]*fa-image[^"]*"[^>]*><\/i>/gi, replacement: '🖼️' },
        { pattern: /<i[^>]*class="[^"]*fa-play[^"]*"[^>]*><\/i>/gi, replacement: '▶️' },
        { pattern: /<i[^>]*class="[^"]*fa-pause[^"]*"[^>]*><\/i>/gi, replacement: '⏸️' },
        { pattern: /<i[^>]*class="[^"]*fa-stop[^"]*"[^>]*><\/i>/gi, replacement: '⏹️' },
      ];

      // Apply all class replacements
      classReplacements.forEach(({ pattern, replacement }) => {
        processedSvg = processedSvg.replace(pattern, replacement);
      });

      // Insert the processed SVG content
      tempContainer.innerHTML = processedSvg;

      // Add Font Awesome stylesheet (for icons inside the SVG/HTML)
      const faHref = 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.5.2/css/all.min.css';
      let faLink = document.querySelector(`link[href="${faHref}"]`) as HTMLLinkElement | null;
      if (!faLink) {
        faLink = document.createElement('link');
        faLink.rel = 'stylesheet';
        faLink.href = faHref;
        // Use anonymous CORS so fonts can be fetched by html2canvas
        faLink.crossOrigin = 'anonymous';
        document.head.appendChild(faLink);
      }

      document.body.appendChild(tempContainer);

      // Wait for fonts to be ready
      try {
        await (document as any).fonts?.ready;
        // Explicitly request FA faces
        await Promise.allSettled([
          (document as any).fonts?.load?.("400 16px 'Font Awesome 6 Free'"),
          (document as any).fonts?.load?.("900 16px 'Font Awesome 6 Free'"),
          (document as any).fonts?.load?.("400 16px 'Font Awesome 6 Brands'")
        ]);
      } catch {}

      // Give the layout a tick to paint
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      // Render to canvas
      const scale = Math.max(2, Math.min(3, window.devicePixelRatio || 2));
      const canvas = await html2canvas(tempContainer, {
        backgroundColor: '#ffffff',
        scale,
        useCORS: true,
        allowTaint: false,
        width: exportWidth,
        height: exportHeight,
        onclone: (doc) => {
          // Ensure FA is present in the cloned document too
          if (!doc.querySelector(`link[href="${faHref}"]`)) {
            const l = doc.createElement('link');
            l.rel = 'stylesheet';
            l.href = faHref;
            (l as any).crossOrigin = 'anonymous';
            doc.head.appendChild(l);
          }
        },
      });

      // Clean up the temporary element
      document.body.removeChild(tempContainer);

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Failed to generate PNG. Try downloading as SVG instead.');
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `visual-${Date.now()}-${index + 1}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('PNG downloaded with icons rendered correctly!');
      }, 'image/png', 0.95);
    } catch (error) {
      console.error('PNG download error:', error);

      // Fallback to simple rasterization of the SVG
      try {
        const v = generatedVisuals[index];
        const { dimensions } = getAspectRatioInfo(aspectRatio);
        const [expW, expH] = dimensions.split('x').map((n) => parseInt(n, 10));
        const exportWidth = Number.isFinite(expW) ? expW : 1200;
        const exportHeight = Number.isFinite(expH) ? expH : 900;

        const canvas = document.createElement('canvas');
        canvas.width = exportWidth * 2;
        canvas.height = exportHeight * 2;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const img = new Image();
        img.crossOrigin = 'anonymous';
        const svgBlob = new Blob([v.svg], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);

        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `visual-${Date.now()}-${index + 1}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              toast.success('PNG downloaded (fallback, icons may vary)');
            }
          }, 'image/png');
          URL.revokeObjectURL(svgUrl);
        };
        img.onerror = () => {
          URL.revokeObjectURL(svgUrl);
          toast.error('PNG download failed. Please try SVG download instead.');
        };
        img.src = svgUrl;
      } catch (fallbackError) {
        console.error('Fallback PNG download failed:', fallbackError);
        toast.error('PNG download failed. Please try SVG download instead.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-primary">Visual Creator</h1>
                  <p className="text-sm text-muted-foreground">
                    Generate stunning visuals with AI
                  </p>
                </div>
              </div>
            </div>
            <Navigation />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Panel */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5" />
                  Create Your Visual
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Prompt Input */}
                <div className="space-y-2">
                  <Label htmlFor="prompt">Describe your visual</Label>
                  <Textarea
                    id="prompt"
                    placeholder="A majestic mountain landscape at sunset with golden light reflecting on a crystal clear lake..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                </div>

                {/* Visual Type Selection */}
                <div className="space-y-3">
                  <Label>Visual Type (Select up to 4)</Label>
                  
                  {/* Auto Option */}
                  <button
                    onClick={() => toggleVisualType('auto')}
                    className={`
                      w-full p-3 rounded-lg border-2 text-left transition-all duration-200
                      ${visualType === 'auto' 
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/20' 
                        : 'border-border bg-card hover:border-primary/50'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Auto (AI Decides)</div>
                        <div className="text-xs text-muted-foreground">Let AI choose the best visual type</div>
                      </div>
                      <div className={`
                        w-4 h-4 rounded-full border-2
                        ${visualType === 'auto' 
                          ? 'border-primary bg-primary' 
                          : 'border-muted-foreground/30'
                        }
                      `}>
                        {visualType === 'auto' && (
                          <div className="w-full h-full rounded-full bg-white scale-[0.4]" />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Multi-select Visual Types */}
                  {selectedVisualTypes.length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm text-muted-foreground mb-2">
                        Selected: {selectedVisualTypes.length}/4
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedVisualTypes.map(type => (
                          <Button
                            key={type}
                            variant="secondary"
                            size="sm"
                            onClick={() => toggleVisualType(type)}
                            className="h-7 text-xs"
                          >
                            {visualTypes.find(v => v.value === type)?.label}
                            <span className="ml-1 text-xs">×</span>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {visualTypes
                      .filter(type => type.value !== 'auto')
                      .map((type) => {
                        const isSelected = selectedVisualTypes.includes(type.value);
                        const isDisabled = !isSelected && selectedVisualTypes.length >= 4;
                        
                        return (
                          <button
                            key={type.value}
                            onClick={() => !isDisabled && toggleVisualType(type.value)}
                            disabled={isDisabled}
                            className={`
                              p-2 rounded border text-left text-sm transition-all duration-200
                              ${isSelected 
                                ? 'border-primary bg-primary/10 text-primary' 
                                : isDisabled
                                ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                                : 'border-border bg-card hover:border-primary/50 hover:bg-primary/5'
                              }
                            `}
                          >
                            <div className="flex items-center justify-between">
                              <span className="truncate">{type.label}</span>
                              {isSelected && (
                                <div className="w-3 h-3 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>

                {/* Aspect Ratio */}
                <div className="space-y-2">
                  <Label>Aspect Ratio</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {aspectRatios.map((ratio) => {
                      const isSelected = aspectRatio === ratio.value;
                      
                      // Calculate dimensions for visual representation
                      const getShapeDimensions = (value: string) => {
                        const [w, h] = value.split(':').map(Number);
                        const scale = 40; // base scale
                        if (w > h) {
                          return { width: scale, height: (h/w) * scale };
                        } else {
                          return { width: (w/h) * scale, height: scale };
                        }
                      };
                      
                      const dimensions = getShapeDimensions(ratio.value);
                      
                      return (
                        <button
                          key={ratio.value}
                          onClick={() => setAspectRatio(ratio.value)}
                          className={`
                            p-3 rounded-lg border-2 transition-all duration-200 text-left
                            ${isSelected 
                              ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                              : 'border-border bg-card hover:border-primary/50 hover:bg-primary/5'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            {/* Visual shape representation */}
                            <div className="flex items-center justify-center w-12 h-12">
                              <div
                                className={`
                                  rounded-sm border-2 flex-shrink-0
                                  ${isSelected 
                                    ? 'bg-primary border-primary' 
                                    : 'bg-muted border-muted-foreground/30'
                                  }
                                `}
                                style={{
                                  width: `${dimensions.width}px`,
                                  height: `${dimensions.height}px`
                                }}
                              />
                            </div>
                            
                            {/* Text labels */}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{ratio.value}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {ratio.label.replace(` (${ratio.value})`, '')}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {ratio.size}
                              </div>
                            </div>
                            
                            {/* Selection indicator */}
                            <div className={`
                              w-4 h-4 rounded-full border-2 flex-shrink-0
                              ${isSelected 
                                ? 'border-primary bg-primary' 
                                : 'border-muted-foreground/30'
                              }
                            `}>
                              {isSelected && (
                                <div className="w-full h-full rounded-full bg-white scale-[0.4]" />
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating || streamingState.isGenerating || !prompt.trim()}
                  className="w-full"
                  size="lg"
                >
                  {(isGenerating || streamingState.isGenerating) ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {streamingState.isGenerating ? "Generating..." : "Generating..."}
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Visual{numOutputs !== "1" ? "s" : ""}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Output Panel */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Generated Visuals</CardTitle>
              </CardHeader>
              <CardContent>
                {generatedVisuals.length === 0 && !isGenerating && !streamingState.isGenerating ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Your generated visuals will appear here</p>
                  </div>
                ) : (isGenerating || streamingState.isGenerating) ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
                    <p className="text-muted-foreground">{streamingState.isGenerating ? 'Streaming your visual...' : 'Creating your visuals...'}</p>
                    {streamingState.svg && (
                      <div className="mt-6 flex justify-center">
                        <div className="border border-border rounded-lg overflow-hidden bg-white shadow-sm p-2">
                          <div className="w-[480px] max-w-full [&>svg]:w-full [&>svg]:h-auto" dangerouslySetInnerHTML={{ __html: sanitizeSvg(streamingState.svg) }} />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {generatedVisuals.map((v, index) => {
                      // Calculate aspect ratio dimensions for display
                      const getDisplayDimensions = (aspectRatio: string) => {
                        const ratioMap: Record<string, { w: number; h: number }> = {
                          '1:1': { w: 400, h: 400 },
                          '16:9': { w: 480, h: 270 },
                          '9:16': { w: 270, h: 480 },
                          '4:3': { w: 400, h: 300 },
                          '3:4': { w: 300, h: 400 },
                        };
                        return ratioMap[aspectRatio] || ratioMap['4:3'];
                      };
                      
                      const dimensions = getDisplayDimensions(aspectRatio);
                      
                      return (
                        <div key={index} className="relative group">
                          <div className="flex flex-col items-center space-y-2">
                            <div className="text-xs text-muted-foreground">
                              {aspectRatio} ratio • {aspectRatios.find(r => r.value === aspectRatio)?.size}
                            </div>
                            <div 
                              className="border border-border rounded-lg overflow-hidden bg-white shadow-sm"
                              style={{
                                width: `${dimensions.w}px`,
                                height: `${dimensions.h}px`,
                                maxWidth: '100%'
                              }}
                            >
                              <div 
                                className="w-full h-full flex items-center justify-center"
                                style={{
                                  width: '100%',
                                  height: '100%'
                                }}
                              >
                                <div
                                  className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain"
                                  dangerouslySetInnerHTML={{ __html: sanitizeSvg(v.svg) }}
                                />
                              </div>
                            </div>
                          </div>
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => copyToClipboard(v.svg, `svg-${index}`)}
                                  className="h-8 w-8 p-0"
                                  title="Copy SVG code"
                                >
                                  {copiedItems.has(`svg-${index}`) ? (
                                    <Check className="w-3 h-3 text-green-400" />
                                  ) : (
                                    <Copy className="w-3 h-3" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => requestFixUpdate(v, index)}
                                  className="h-8 w-8 p-0"
                                  title="Fix/Update Visual"
                                >
                                  <Wand2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => saveToContent(v, index, currentProjectId)}
                                  className="h-8 w-8 p-0"
                                  title="Save to My Content"
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => downloadSvg(index)}
                                  className="h-8 w-8 p-0"
                                  title="Download SVG"
                                >
                                  <FileImage className="w-3 h-3" />
                                </Button>
                                 <Button
                                   size="sm"
                                   variant="secondary"
                                   onClick={() => downloadPng(index)}
                                   className="h-8 w-8 p-0"
                                   title="Download PNG"
                                 >
                                   <ImageIcon className="w-3 h-3" />
                                 </Button>
                              </div>
                            </div>
                        </div>
                      );
                    })}
                  </div>
                )}

              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <UpgradeDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog} />
    </div>
  );
};

export default VisualCreator;